import { Location } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  EnvironmentInjector,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  Injectable,
  InjectionToken,
  Injector,
  isDevMode,
  makeEnvironmentProviders,
  ProviderToken,
  Type,
} from '@angular/core';
import {
  NoPreloading,
  provideRouter,
  Router,
  UrlSerializer,
  withComponentInputBinding,
  withPreloading,
} from '@angular/router';
import { AUTH_SETTINGS } from '@indice/ng-auth';
import { ModalService } from '@indice/ng-components';
import {
  APP_ENVIRONMENT,
  APP_SETTINGS,
  APP_SETTINGS_MANIFEST_URL,
  IAppSettings,
  provideAppSettings,
  SettingsFacadeService,
} from '@indice/ng-config';
import { provideTransloco } from '@jsverse/transloco';
import {
  GetLangParams,
  provideTranslocoPersistLang,
} from '@jsverse/transloco-persist-lang';
import { CookieService } from 'ngx-cookie-service';
import { NG_PROGRESS_CONFIG } from 'ngx-progressbar';
import { NgProgressHttpModule } from 'ngx-progressbar/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { AuthInterceptor } from './core/services/auth.interceptor';
import { SERVICES_DMS_API_BASE_URL } from './core/services/dms-api-service';
import { ErrorInterceptor } from './core/services/errors/errors.interceptor';
import { LanguageService } from './core/services/language.service';
import { SERVICES_LOANS_API_BASE_URL } from './core/services/loans-api-service';
import { LocaleInterceptor } from './core/services/locale.interceptor';
import { LoggerService } from './core/services/logger.service';
import { SERVICES_PAYMENTS_API_BASE_URL } from './core/services/payments-api-service';
import { COOKIEBOT_KEY } from './features/cookie-settings/cookie-settings.component';
import { CASES_API_BASE_URL } from './services/cases-api.service';
import { CMS_API_BASE_URL } from './services/cms-api.service';
import { CMS_API_KEY } from './services/cms.service';
import { IDENTITY_API_BASE_URL } from './services/identity-api.service';
import { NBG_PORTAL_API_BASE_URL } from './services/loan.service';
import { MATERPORT_SKD_KEY } from './services/matterport.service';
import { MESSAGING_API_BASE_URL } from './services/messaging-api.service';
import {
  PORTAL_API_BASE_URL,
  PortalApiService,
} from './services/portal-api.service';
import { LanguageRouterLinkDirective } from './shared/directives/language-router-link.directive';
import { TranslocoHttpLoader } from './transloco-loader';
import {
  LANGUAGE_PICKER_HAS_TRANSLATIONS,
  SHOW_LANGUAGE_PICKER,
} from './services/language-selecetro.service';

/**
 * Service to read asynchronously if the APP_SETTINGS are set by the ng-config library,
 * in order to let other services instantiate and use their injected tokens.
 */
@Injectable({ providedIn: 'root' })
export class SettingsReadyService {
  private readySubject = new BehaviorSubject<boolean>(false);
  ready$ = this.readySubject.asObservable();

  constructor() {
    const settingsState = inject(SettingsFacadeService);
    if (
      settingsState.settings() &&
      Object.keys(settingsState.settings() as IAppSettings).length > 0
    ) {
      this.readySubject.next(true);
    }
  }

  markReady() {
    this.readySubject.next(true);
  }
}

@Injectable({ providedIn: 'root' })
export class UrlInitializer {
  constructor(
    private router: Router,
    private urlSerializer: UrlSerializer,
    private location: Location,
    private injector: Injector
  ) {}

  initialize(): Promise<void> {
    return new Promise<void>((resolve) => {
      const url = this.location.path(true); // Get the current url
      this.urlSerializer.parse(url); // Force parsing with the custom serializer.
      this.router.parseUrl(url); // Force the router to parse the url.
      resolve();
    });
  }
}

export function customGetLang({
  cachedLang,
  browserLang,
  cultureLang,
  defaultLang,
}: GetLangParams): string {
  const languageService = inject(LanguageService);
  const localStorageLangValue = localStorage.getItem('translocoLang');
  if (
    localStorageLangValue &&
    languageService.getCurrentLangSnapshot() !== localStorageLangValue
  ) {
    languageService.setCurrentLang(localStorageLangValue);
  }
  return (
    languageService.getCurrentLangSnapshot() ||
    cachedLang ||
    browserLang ||
    cultureLang ||
    defaultLang
  );
}

/**
 * If we need to provide a new Token we just add it here in the array easily.
 * @returns EnvironmentProviders array
 */
export function dynamicAppSettingsProviders(): EnvironmentProviders {
  const mappings: { provide: any; path: string }[] = [
    { provide: AUTH_SETTINGS, path: 'auth_settings' },
    { provide: PORTAL_API_BASE_URL, path: 'api_url' },
    { provide: IDENTITY_API_BASE_URL, path: 'auth_settings.authority' },
    { provide: CASES_API_BASE_URL, path: 'cases_url' },
    { provide: MESSAGING_API_BASE_URL, path: 'msg_url' },
    { provide: CMS_API_BASE_URL, path: 'cms_url' },
    { provide: CMS_API_KEY, path: 'cmsApiKey' },
    { provide: SERVICES_PAYMENTS_API_BASE_URL, path: 'services_api_url' },
    { provide: SERVICES_DMS_API_BASE_URL, path: 'services_api_url' },
    { provide: SERVICES_LOANS_API_BASE_URL, path: 'services_api_url' },
    { provide: MATERPORT_SKD_KEY, path: 'matterport_key' },
    { provide: COOKIEBOT_KEY, path: 'cookiebot_key' },
    { provide: NBG_PORTAL_API_BASE_URL, path: 'nbg_portal_api_url' },
    { provide: SHOW_LANGUAGE_PICKER, path: 'show_language_picker' },
    {
      provide: LANGUAGE_PICKER_HAS_TRANSLATIONS,
      path: 'language_picker_has_translations',
    },
  ];

  return makeEnvironmentProviders(
    mappings.map(({ provide, path }) => ({
      provide,
      useFactory: () => {
        const settings = inject(APP_SETTINGS) as any;
        return get(settings, path);
      },
      deps: [APP_SETTINGS],
    }))
  );
}

// Helper to safely get nested settings and warn if missing
function get(obj: any, path: string): any {
  const value = path.split('.').reduce((o, p) => (o ? o[p] : undefined), obj);

  if (value === undefined || value === null) {
    const env = inject(EnvironmentInjector, { optional: true });
    const isDev = !env || !('production' in env) || env['production'] === false;

    if (isDev) {
      console.warn(`[APP_SETTINGS WARNING] Missing setting at path: "${path}"`);
      // Optional: if you want to be very strict
      throw new Error(
        `[APP_SETTINGS ERROR] Missing setting at path: "${path}"`
      );
    }
  }

  return value ?? '';
}

@Injectable({ providedIn: 'root' })
export class LazyLoadServiceFactory<T> {
  private servicePromise: Promise<T> | undefined;
  private serviceInstance!: T;
  private readySubject = new BehaviorSubject<boolean>(false);
  ready$ = this.readySubject.asObservable();
  private serviceClass!: Type<T>;
  private dependencies: ProviderToken<any>[] = [];

  constructor(
    private logger: LoggerService,
    private injector: Injector,
    private settingsReadyService: SettingsReadyService
  ) {}

  configure(serviceClass: Type<T>, dependencies: any[] = []): void {
    this.serviceClass = serviceClass;
    this.dependencies = dependencies;
  }

  createService(): T {
    if (!this.serviceClass) {
      throw new Error('Service class not configured. Call configure() first.');
    }

    if (!this.servicePromise) {
      this.settingsReadyService.ready$.subscribe((ready) => {
        if (ready) {
          const resolvedDependencies = this.dependencies.map((dep) => {
            if (typeof dep === 'string') {
              return this.injector.get(dep);
            }
            return this.injector.get(dep);
          });

          this.serviceInstance = new this.serviceClass(...resolvedDependencies);
          this.servicePromise = Promise.resolve(this.serviceInstance);
          this.readySubject.next(true);
        } else {
          this.logger.info('Settings not ready, waiting...');
        }
      });
    }

    return this.serviceInstance;
  }
}

export function createLazyServiceFactory<T>(
  serviceClass: Type<T>,
  dependencies: ProviderToken<any>[]
): (
  logger: LoggerService,
  injector: Injector,
  settingsReady: SettingsReadyService
) => LazyLoadServiceFactory<T> {
  return (
    logger: LoggerService,
    injector: Injector,
    settingsReady: SettingsReadyService
  ) => {
    const factory = new LazyLoadServiceFactory<T>(
      logger,
      injector,
      settingsReady
    );
    factory.configure(serviceClass, dependencies);
    return factory;
  };
}

export const PORTAL_API_SERVICE_FACTORY = new InjectionToken<
  LazyLoadServiceFactory<PortalApiService>
>('PortalApiServiceFactory');

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppSettings(),
    // If you want to override the APP_SETTINGS_MANIFEST_URL uncomment below and change the path
    // {
    //   provide: APP_SETTINGS_MANIFEST_URL,
    //   useValue: '/assets/app-settings.manifest.json',
    // },
    { provide: APP_ENVIRONMENT, useValue: environment },
    dynamicAppSettingsProviders(),
    {
      provide: PORTAL_API_SERVICE_FACTORY,
      useFactory: createLazyServiceFactory<PortalApiService>(PortalApiService, [
        HttpClient,
        PORTAL_API_BASE_URL,
      ]),
      deps: [LoggerService, Injector, SettingsReadyService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (urlInitializer: UrlInitializer) => () =>
        urlInitializer.initialize(),
      deps: [UrlInitializer],
      multi: true,
    },
    provideRouter(
      routes,
      withPreloading(NoPreloading),
      withComponentInputBinding()
    ),
    ModalService,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LocaleInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: SearchHistoryInterceptor, multi: true },
    provideHttpClient(),
    provideTranslocoPersistLang({
      getLangFn: customGetLang,
      storage: {
        useValue: localStorage,
      },
    }),
    provideTransloco({
      config: {
        availableLangs: ['en', 'el', 'translate'],
        defaultLang: 'el',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    {
      provide: NG_PROGRESS_CONFIG,
      useValue: {
        thick: true,
        color: '#50575f',
      },
    },
    importProvidersFrom(NgProgressHttpModule),
    provideHttpClient(withInterceptorsFromDi()),
    CookieService,
    LanguageRouterLinkDirective,
  ],
};
